import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { useBuildingTypes } from '../../../shared/hooks/useBuildingTypes';
import { Batiment } from '../../../shared/types/game';
import BuildingDetailModal from './BuildingDetailModal';

interface BuildingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  buildings: Batiment[];
  onBuildingUpdate?: (updatedBuilding: Batiment) => void;
  onBuildingsChange?: (buildings: Batiment[]) => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  pompier: '#e24b4a',
  hopital: '#378add',
  fdo:     '#534ab7',
  cta:     '#ba7517',
  prison:  '#888780',
};

const TYPE_ICON_BG: Record<string, string> = {
  pompier: '#ffeaea',
  hopital: '#eaf3ff',
  fdo:     '#e8eeff',
  cta:     '#fff8e0',
  prison:  '#f1efe8',
};

const TYPE_ICON_COLOR: Record<string, string> = {
  pompier: '#a32d2d',
  hopital: '#185fa5',
  fdo:     '#3c3489',
  cta:     '#854f0b',
  prison:  '#5f5e5a',
};

const getBuildingIconPath = (buildingType: string): string => {
  switch (buildingType) {
    case 'pompier': return '/images/feu.png';
    case 'hopital':  return '/images/crois.png';
    case 'fdo':      return '/images/forces-de-lordre.png';
    case 'prison':   return '/images/prison.png';
    case 'cta':      return '/images/antenne-cta.png';
    default:         return '/images/icon-bat.png';
  }
};

// Cache géocodage
const addressCache = new Map<string, string>();
const getAddressFromCoordinates = async (lat: number, lng: number): Promise<string | null> => {
  const key = `${lat},${lng}`;
  if (addressCache.has(key)) return addressCache.get(key)!;
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      { headers: { 'User-Agent': 'OpSecours-Game/1.0' } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.address) return data.display_name || null;
    const a = data.address;
    const parts: string[] = [];
    if (a.road) parts.push(a.road);
    if (a.house_number) parts[0] = `${a.house_number} ${parts[0] || ''}`.trim();
    if (a.postcode) parts.push(a.postcode);
    if (a.city || a.town || a.village) parts.push(a.city || a.town || a.village);
    const full = parts.join(', ') || data.display_name;
    addressCache.set(key, full);
    return full;
  } catch { return null; }
};

// ─── Sub-components ──────────────────────────────────────────────────────────

interface MiniBarProps { value: number; total: number; color: string }
const MiniBar: React.FC<MiniBarProps> = ({ value, total, color }) => (
  <div className="h-0.5 w-full bg-zinc-700/60 rounded-full mt-1.5 overflow-hidden">
    <div className="h-full rounded-full" style={{ width: total > 0 ? `${Math.round((value / total) * 100)}%` : '0%', background: color }} />
  </div>
);

// ─── Main component ──────────────────────────────────────────────────────────

const BuildingsModal: React.FC<BuildingsModalProps> = ({
  isOpen,
  onClose,
  buildings,
  onBuildingUpdate,
  onBuildingsChange,
}) => {
  const { t } = usePageTranslation('game', 'Map');
  const { buildingTypes: typesFromApi, loading: loadingTypes } = useBuildingTypes();

  const [activeType, setActiveType]       = useState<string>('');
  const [searchQuery, setSearchQuery]     = useState('');
  const [filterActif, setFilterActif]     = useState(false);
  const [filterEngaged, setFilterEngaged] = useState(false);
  const [addresses, setAddresses]         = useState<Record<string, string>>({});
  const [loadingAddr, setLoadingAddr]     = useState<Set<string>>(new Set());
  const addressesRef  = useRef(addresses);
  const loadingRef    = useRef(loadingAddr);

  const [selectedBuilding, setSelectedBuilding] = useState<Batiment | null>(null);
  const [detailModalOpen,  setDetailModalOpen]  = useState(false);
  const [detailInitialTab, setDetailInitialTab] = useState<'info' | 'gerer'>('info');

  useEffect(() => { addressesRef.current = addresses; }, [addresses]);
  useEffect(() => { loadingRef.current = loadingAddr; }, [loadingAddr]);

  useEffect(() => {
    if (typesFromApi.length > 0 && !activeType) setActiveType(typesFromApi[0].id);
  }, [typesFromApi, activeType]);

  // Fermer Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Scroll lock
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  // Filtered list
  const filteredBuildings = useMemo(() => {
    if (!activeType) return [];
    let list = buildings.filter((b) => b.type === activeType);
    if (filterActif) list = list.filter((b) => b.actif);
    if (filterEngaged) list = list.filter(() => false); // placeholder — need vehicle data
    const q = searchQuery.toLowerCase().trim();
    if (!q) return list;
    return list.filter((b) =>
      b.nom.toLowerCase().includes(q) ||
      b.localisation?.toLowerCase().includes(q) ||
      addresses[b.localisation ?? '']?.toLowerCase().includes(q)
    );
  }, [buildings, activeType, searchQuery, filterActif, filterEngaged, addresses]);

  // Géocoder les bâtiments visibles
  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      for (const b of filteredBuildings) {
        const loc = b.localisation;
        if (!loc) continue;
        if (addressesRef.current[loc] || loadingRef.current.has(loc)) continue;
        const [latStr, lngStr] = loc.split(',');
        const lat = parseFloat(latStr?.trim());
        const lng = parseFloat(lngStr?.trim());
        if (isNaN(lat) || isNaN(lng)) continue;
        setLoadingAddr((prev) => new Set(prev).add(loc));
        const addr = await getAddressFromCoordinates(lat, lng);
        if (addr) setAddresses((prev) => ({ ...prev, [loc]: addr }));
        setLoadingAddr((prev) => { const s = new Set(prev); s.delete(loc); return s; });
      }
    };
    load();
  }, [isOpen, filteredBuildings]);

  // Stats globales
  const globalStats = useMemo(() => ({
    total:     buildings.length,
    actifs:    buildings.filter((b) => b.actif).length,
    vehicules: 0, // pas disponible sans fetch
    personnel: 0,
  }), [buildings]);

  // CTA name resolver
  const ctaByBuildingId = useMemo(() => {
    const m = new Map<number, string>();
    buildings.filter((b) => b.type === 'cta').forEach((cta) => {
      buildings.filter((b) => b.cta_id === cta.id).forEach((b) => m.set(b.id, cta.nom));
    });
    return m;
  }, [buildings]);

  const openDetail = useCallback((b: Batiment, tab: 'info' | 'gerer') => {
    setDetailInitialTab(tab);
    setDetailModalOpen(true);
    // selectedBuilding already set by the card click
    setSelectedBuilding(b);
  }, []);

  if (!isOpen) return null;

  const currentTypeName = typesFromApi.find((s) => s.id === activeType)?.name ?? activeType;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/65 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      {/* Modal */}
      <div className="relative z-50 bg-zinc-950 rounded-xl shadow-2xl w-full max-w-6xl max-h-[88vh] overflow-hidden border border-zinc-800/80 flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-zinc-900/60 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center">
              <svg className="w-5 h-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Gestion des bâtiments</h2>
              <p className="text-xs text-zinc-500 mt-0.5">{globalStats.total} bâtiments · {typesFromApi.length} types</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
            aria-label="Fermer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-4 border-b border-zinc-800 shrink-0" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
          {[
            { label: 'Bâtiments total', value: globalStats.total,  color: 'text-zinc-100' },
            { label: 'Actifs',          value: globalStats.actifs,  color: 'text-emerald-400' },
            { label: 'Inactifs',        value: globalStats.total - globalStats.actifs, color: 'text-zinc-400' },
            { label: 'Types',           value: typesFromApi.length, color: 'text-sky-400' },
          ].map((s, i) => (
            <div key={i} className={`px-5 py-3 bg-zinc-900/40 ${i < 3 ? 'border-r border-zinc-800' : ''}`}>
              <p className={`text-xl font-medium ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex flex-1 min-h-0">

          {/* ── Sidebar ── */}
          <div className="w-48 shrink-0 border-r border-zinc-800 bg-zinc-900/30 flex flex-col">
            {/* Search */}
            <div className="p-3 border-b border-zinc-800">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 pl-8 text-xs text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  aria-label="Rechercher un bâtiment"
                />
                <svg className="absolute left-2.5 top-2 w-3.5 h-3.5 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Types */}
            <div className="flex-1 overflow-y-auto p-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 px-2 mb-1.5">Types</p>
              {loadingTypes ? (
                <p className="text-xs text-zinc-500 px-2">Chargement…</p>
              ) : (
                typesFromApi.map((type) => {
                  const count = buildings.filter((b) => b.type === type.id).length;
                  const isActive = activeType === type.id;
                  const dotColor = TYPE_COLORS[type.id] ?? '#888';
                  return (
                    <button
                      key={type.id}
                      onClick={() => { setActiveType(type.id); setSelectedBuilding(null); }}
                      className={`flex items-center gap-2 w-full px-2 py-2 rounded-lg text-left text-xs mb-0.5 transition-colors ${
                        isActive
                          ? 'bg-zinc-800 text-white font-medium'
                          : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200'
                      }`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
                      <span className="flex-1 truncate">{type.name}</span>
                      <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${
                        isActive ? 'bg-zinc-700 text-zinc-300' : 'bg-zinc-800 text-zinc-500'
                      }`}>{count}</span>
                    </button>
                  );
                })
              )}

              <div className="border-t border-zinc-800 my-2" />
              <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 px-2 mb-1.5">Filtres rapides</p>

              <button
                onClick={() => setFilterActif((v) => !v)}
                className={`flex items-center gap-2 w-full px-2 py-2 rounded-lg text-xs mb-0.5 transition-colors ${
                  filterActif ? 'bg-emerald-500/15 text-emerald-400' : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200'
                }`}
              >
                <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Actifs seulement
              </button>
            </div>
          </div>

          {/* ── Building list ── */}
          <div className="flex-1 overflow-y-auto p-3">
            {!loadingTypes && (
              <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-3 px-1">
                {currentTypeName} · {filteredBuildings.length} résultat{filteredBuildings.length !== 1 ? 's' : ''}
              </p>
            )}

            {loadingTypes ? (
              <p className="text-center text-zinc-500 text-sm py-12">Chargement…</p>
            ) : filteredBuildings.length === 0 ? (
              <div className="text-center text-zinc-500 py-12">
                <p className="text-sm mb-1">{searchQuery ? 'Aucun résultat' : `Aucun ${currentTypeName.toLowerCase()} pour le moment`}</p>
                <p className="text-xs">{searchQuery ? 'Modifiez votre recherche' : t('buildingsModal.buildBuildingHint')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredBuildings.map((b) => {
                  const isSelected = selectedBuilding?.id === b.id;
                  const loc = b.localisation ?? '';
                  const addr = addresses[loc];
                  const totalCapa = (b.postes_disponibles ?? 0) + (b.postes_occupes ?? 0);
                  const iconBg    = TYPE_ICON_BG[b.type]    ?? '#27272a';
                  const iconColor = TYPE_ICON_COLOR[b.type] ?? '#a1a1aa';

                  return (
                    <div
                      key={b.id}
                      onClick={() => setSelectedBuilding(isSelected ? null : b)}
                      className={`rounded-xl border px-4 py-3 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-sky-500/60 bg-zinc-800/60 shadow-[0_0_0_1px_rgba(56,189,248,0.15)]'
                          : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700 hover:bg-zinc-800/40'
                      }`}
                    >
                      {/* Top row */}
                      <div className="flex items-center gap-3 mb-3">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                          style={{ backgroundColor: iconBg }}
                        >
                          <img src={getBuildingIconPath(b.type)} alt={b.type} className="w-5 h-5" style={{ filter: `drop-shadow(0 0 0 ${iconColor})` }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-zinc-100 truncate">{b.nom}</p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${
                              b.actif
                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
                                : 'bg-zinc-800 text-zinc-500 border-zinc-700/50'
                            }`}>
                              {b.actif ? 'Actif' : 'Inactif'}
                            </span>
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/25">
                              Niv. {b.niveau}
                            </span>
                            {addr ? (
                              <span className="text-[10px] text-zinc-500 truncate">{addr}</span>
                            ) : loadingAddr.has(loc) ? (
                              <span className="text-[10px] text-zinc-600 italic">Localisation…</span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {/* Stats row */}
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-zinc-800/60 rounded-lg px-2.5 py-2">
                          <p className="text-sm font-medium text-zinc-100">{b.capacite_vehicules ?? 0}</p>
                          <p className="text-[10px] text-zinc-500">Véhicules</p>
                          <MiniBar value={b.capacite_vehicules ?? 0} total={b.capacite_vehicules ?? 1} color="#10b981" />
                        </div>
                        <div className="bg-zinc-800/60 rounded-lg px-2.5 py-2">
                          <p className="text-sm font-medium text-zinc-100">{b.postes_occupes ?? 0}</p>
                          <p className="text-[10px] text-zinc-500">Personnel</p>
                          <MiniBar value={b.postes_occupes ?? 0} total={totalCapa} color="#3b82f6" />
                        </div>
                        <div className="bg-zinc-800/60 rounded-lg px-2.5 py-2">
                          <p className="text-sm font-medium text-zinc-100">{b.postes_disponibles ?? 0}</p>
                          <p className="text-[10px] text-zinc-500">Postes libres</p>
                          <MiniBar value={b.postes_disponibles ?? 0} total={totalCapa} color="#6366f1" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Detail panel ── */}
          {selectedBuilding && (
            <div className="w-64 shrink-0 border-l border-zinc-800 bg-zinc-900/20 flex flex-col overflow-hidden">

              {/* Header */}
              <div className="p-4 border-b border-zinc-800">
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: TYPE_ICON_BG[selectedBuilding.type] ?? '#27272a' }}
                  >
                    <img src={getBuildingIconPath(selectedBuilding.type)} alt={selectedBuilding.type} className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-100 truncate">{selectedBuilding.nom}</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{typesFromApi.find((t) => t.id === selectedBuilding.type)?.name ?? selectedBuilding.type}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                    selectedBuilding.actif
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
                      : 'bg-zinc-800 text-zinc-500 border-zinc-700/50'
                  }`}>{selectedBuilding.actif ? 'Actif' : 'Inactif'}</span>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/25">
                    Niveau {selectedBuilding.niveau}
                  </span>
                </div>
              </div>

              {/* Capacités */}
              <div className="p-4 border-b border-zinc-800">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-2.5">Capacités</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Véhicules',   value: selectedBuilding.capacite_vehicules ?? 0 },
                    { label: 'Personnel',   value: selectedBuilding.postes_occupes ?? 0 },
                    { label: 'Postes',      value: `${selectedBuilding.postes_occupes ?? 0}/${(selectedBuilding.postes_disponibles ?? 0) + (selectedBuilding.postes_occupes ?? 0)}` },
                    { label: 'Places véh.', value: `${selectedBuilding.capacite_vehicules ?? 0}/${selectedBuilding.capacite_vehicules ?? 0}` },
                  ].map((s) => (
                    <div key={s.label} className="bg-zinc-800/60 rounded-lg px-2.5 py-2">
                      <p className="text-sm font-medium text-zinc-100">{s.value}</p>
                      <p className="text-[10px] text-zinc-500 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* XP */}
              <div className="p-4 border-b border-zinc-800">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Progression</p>
                <div className="bg-zinc-800/60 rounded-lg px-3 py-2.5">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-zinc-400">XP</span>
                    <span className="text-sky-400 font-medium">{selectedBuilding.xp ?? 0} / {selectedBuilding.niveau * 10}</span>
                  </div>
                  <div className="h-1 bg-zinc-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-sky-500 rounded-full"
                      style={{ width: `${Math.min(100, Math.round(((selectedBuilding.xp ?? 0) % 10) * 10))}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-1.5">
                    {10 - ((selectedBuilding.xp ?? 0) % 10)} XP pour le niveau {selectedBuilding.niveau + 1}
                  </p>
                </div>
              </div>

              {/* Localisation */}
              <div className="p-4 flex-1 overflow-y-auto">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Localisation</p>
                <div className="bg-zinc-800/60 rounded-lg px-3 py-2.5 text-xs text-zinc-400 leading-relaxed">
                  {addresses[selectedBuilding.localisation ?? ''] ?? selectedBuilding.localisation ?? '—'}
                </div>
                {selectedBuilding.cta_id && (
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1.5 text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-1">
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      {ctaByBuildingId.get(selectedBuilding.id) ?? `CTA #${selectedBuilding.cta_id}`}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-3 border-t border-zinc-800 grid grid-cols-2 gap-2">
                <button
                  onClick={() => openDetail(selectedBuilding, 'info')}
                  className="rounded-lg border border-zinc-700 bg-zinc-800/60 py-2 text-xs font-medium text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors"
                >
                  Détails
                </button>
                <button
                  onClick={() => openDetail(selectedBuilding, 'gerer')}
                  className="rounded-lg border border-sky-500/40 bg-sky-500/10 py-2 text-xs font-medium text-sky-300 hover:bg-sky-500/20 transition-colors"
                >
                  Gérer
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Detail modal */}
      {detailModalOpen && selectedBuilding && (
        <BuildingDetailModal
          building={selectedBuilding}
          address={addresses[selectedBuilding.localisation ?? '']}
          allBuildings={buildings}
          initialTab={detailInitialTab}
          onClose={() => setDetailModalOpen(false)}
          onBuildingUpdate={(updated) => {
            onBuildingUpdate?.(updated);
            setSelectedBuilding(updated);
          }}
          onBuildingDelete={(deletedId) => {
            onBuildingsChange?.(buildings.filter((b) => String(b.id) !== String(deletedId)));
            setDetailModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default BuildingsModal;
